// Search-syntax parser + keyword catalog for the deck-builder collection
// search bar. Mirrors the Corefield syntax: name:Sir, t:Minion, el:af,
// r:draw, cost:3, cost>=3, etc.

const ELEMENT_LETTER_MAP = { a: 'Air', e: 'Earth', f: 'Fire', w: 'Water' };

export const SEARCH_KEYWORDS = [
  { prefix: 'name',      aliases: ['n'],          description: 'Card name',                kind: 'name' },
  { prefix: 'type',      aliases: ['t'],          description: 'Card type',                kind: 'enum',    enumOptions: ['Avatar', 'Minion', 'Magic', 'Aura', 'Artifact', 'Site'], filterKey: 'typeFilters' },
  { prefix: 'element',   aliases: ['el'],         description: 'Element, combine letters: el:af for Air + Fire', kind: 'element', filterKey: 'elementFilters' },
  { prefix: 'set',       aliases: ['s'],          description: 'Set / expansion',          kind: 'enum',    enumOptions: ['gothic', 'arthurian', 'beta', 'dragonlord'], enumAliases: { goth: 'gothic', gothic: 'gothic', art: 'arthurian', arthurian: 'arthurian', bet: 'beta', beta: 'beta', dra: 'dragonlord', dragon: 'dragonlord', dragonlord: 'dragonlord' }, filterKey: 'setFilters' },
  { prefix: 'rarity',    aliases: ['rar'],        description: 'Rarity',                   kind: 'enum',    enumOptions: ['Ordinary', 'Exceptional', 'Elite', 'Unique'], filterKey: 'rarityFilters' },
  // Rules and Flavor are hidden from autocomplete suggestions because
  // their values are usually multi-word and the bare-token syntax (where
  // a space ends the value) is too easy to mis-type. They're still parseable
  // when typed manually with quotes, e.g. r:"draw a spell", and the advanced
  // panel exposes proper inputs for them.
  { prefix: 'rules',     aliases: ['r'],          description: 'Rules-text substring (use quotes for multiple words)', kind: 'string', filterKey: 'rulesQuery', hideFromSuggestions: true },
  { prefix: 'flavor',    aliases: ['fl'],         description: 'Flavor / subtitle (use quotes for multiple words)',     kind: 'string', filterKey: 'flavorQuery', hideFromSuggestions: true },
  { prefix: 'keyword',   aliases: ['kw'],         description: 'Keyword ability',          kind: 'multi-string', filterKey: 'keywordFilters' },
  { prefix: 'subtype',   aliases: ['sub'],        description: 'Subtype / trait',          kind: 'multi-string', filterKey: 'subtypeFilters' },
  { prefix: 'artist',    aliases: [],             description: 'Artist name',              kind: 'multi-string', filterKey: 'artistFilters' },
  { prefix: 'cost',      aliases: ['c'],          description: 'Mana cost (numeric)',      kind: 'numeric', filterKey: 'costFilter' },
  { prefix: 'attack',    aliases: ['atk', 'pow'], description: 'Attack (numeric)',         kind: 'numeric', filterKey: 'attackFilter' },
  { prefix: 'defense',   aliases: ['def'],        description: 'Defense (numeric)',        kind: 'numeric', filterKey: 'defenseFilter' },
  { prefix: 'life',      aliases: ['hp'],         description: 'Avatar life (numeric)',    kind: 'numeric', filterKey: 'lifeFilter' },
  { prefix: 'threshold', aliases: ['th'],         description: 'Any threshold (numeric)',  kind: 'numeric', filterKey: 'anyThresholdFilter' },
  { prefix: 'at',        aliases: [],             description: 'Air threshold (numeric)',  kind: 'numeric', filterKey: 'airThresholdFilter' },
  { prefix: 'et',        aliases: [],             description: 'Earth threshold (numeric)',kind: 'numeric', filterKey: 'earthThresholdFilter' },
  { prefix: 'ft',        aliases: [],             description: 'Fire threshold (numeric)', kind: 'numeric', filterKey: 'fireThresholdFilter' },
  { prefix: 'wt',        aliases: [],             description: 'Water threshold (numeric)',kind: 'numeric', filterKey: 'waterThresholdFilter' },
  // Valkenhall-specific single-value filters (not in Corefield). These
  // hold one value at a time, so kind=single-enum (replace) rather than
  // enum (add to a Set).
  { prefix: 'scope',     aliases: ['sc'],         description: 'Card pool: deck / owned / all', kind: 'single-enum', enumOptions: ['deck', 'owned', 'all'], enumAliases: { d: 'deck', indeck: 'deck', o: 'owned', a: 'all' }, filterKey: 'cardScope' },
  { prefix: 'finish',    aliases: ['fin', 'foiling'], description: 'Foiling: all / standard / foil', kind: 'single-enum', enumOptions: ['all', 'standard', 'foil'], enumAliases: { s: 'standard', f: 'foil' }, filterKey: 'foilFilter' },
];

const KEYWORD_BY_PREFIX = new Map();
for (const kw of SEARCH_KEYWORDS) {
  KEYWORD_BY_PREFIX.set(kw.prefix, kw);
  for (const alias of kw.aliases) KEYWORD_BY_PREFIX.set(alias, kw);
}

export function getKeywordByPrefix(prefix) {
  return KEYWORD_BY_PREFIX.get(prefix.toLowerCase()) || null;
}

export function getMatchingKeywords(fragment) {
  const q = (fragment || '').toLowerCase();
  const visible = SEARCH_KEYWORDS.filter((k) => !k.hideFromSuggestions);
  if (!q) return visible;
  return visible.filter((k) => {
    if (k.prefix.startsWith(q)) return true;
    return k.aliases.some((a) => a.startsWith(q));
  });
}

export function getMatchingEnumValues(keyword, fragment) {
  if (!keyword || !keyword.enumOptions) return [];
  const q = (fragment || '').toLowerCase();
  return keyword.enumOptions.filter((v) => v.toLowerCase().startsWith(q));
}

export function getElementLetterSuggestions(fragment) {
  const taken = new Set((fragment || '').toLowerCase().split(''));
  return Object.entries(ELEMENT_LETTER_MAP)
    .filter(([letter]) => !taken.has(letter))
    .map(([letter, name]) => ({ letter, name }));
}

export function parseSearchQuery(text) {
  const tokens = [];
  const residual = [];
  let i = 0;
  const len = text.length;

  while (i < len) {
    if (/\s/.test(text[i])) { i++; continue; }

    const result = matchTokenAt(text, i);
    if (result) {
      tokens.push(result.token);
      i = result.end;
    } else {
      const wordStart = i;
      while (i < len && !/\s/.test(text[i])) i++;
      residual.push({ text: text.slice(wordStart, i), start: wordStart, end: i });
    }
  }

  return { tokens, leftover: residual.map((r) => r.text).join(' '), residual };
}

function matchTokenAt(text, start) {
  const m = /^([a-z][a-z]*)([:=<>!]+)/i.exec(text.slice(start));
  if (!m) return null;

  const prefix = m[1].toLowerCase();
  const opRaw = m[2];
  const keyword = KEYWORD_BY_PREFIX.get(prefix);
  if (!keyword) return null;

  let op;
  switch (opRaw) {
    case ':':
    case '=':  op = '='; break;
    case '!=': op = '!='; break;
    case '<':  op = '<'; break;
    case '<=': op = '<='; break;
    case '>':  op = '>'; break;
    case '>=': op = '>='; break;
    default: return null;
  }

  // String / multi-string only support `:` or `=`. Numeric supports the others.
  if (keyword.kind !== 'numeric' && (op !== '=')) return null;

  const valueStart = start + m[0].length;
  if (valueStart >= text.length) return null;

  let value, valueEnd;
  if (text[valueStart] === '"') {
    const closeQuote = text.indexOf('"', valueStart + 1);
    if (closeQuote === -1) {
      value = text.slice(valueStart + 1);
      valueEnd = text.length;
    } else {
      value = text.slice(valueStart + 1, closeQuote);
      valueEnd = closeQuote + 1;
    }
  } else {
    let j = valueStart;
    while (j < text.length && !/\s/.test(text[j])) j++;
    value = text.slice(valueStart, j);
    valueEnd = j;
  }

  if (!value) return null;
  if (keyword.kind === 'numeric' && !/^-?\d+$/.test(value)) return null;

  return {
    token: { prefix, keyword, op, value, raw: text.slice(start, valueEnd), start, end: valueEnd },
    end: valueEnd,
  };
}

export function getActiveTokenContext(text, cursorPos) {
  let i = cursorPos;
  while (i > 0 && !/\s/.test(text[i - 1])) i--;
  const tokenStart = i;
  let j = cursorPos;
  while (j < text.length && !/\s/.test(text[j])) j++;
  const tokenEnd = j;

  const tokenText = text.slice(tokenStart, tokenEnd);
  if (!tokenText) return { kind: 'empty', tokenStart, tokenEnd, fragment: '' };

  const sepMatch = /^([a-z]+)([:=<>!]+)(.*)$/i.exec(tokenText);
  if (sepMatch) {
    const prefix = sepMatch[1].toLowerCase();
    const opRaw = sepMatch[2];
    const valueFragment = sepMatch[3];
    const keyword = KEYWORD_BY_PREFIX.get(prefix);
    if (keyword) {
      return {
        kind: 'value',
        keyword, prefix, opRaw, valueFragment,
        tokenStart, tokenEnd,
        valueStart: tokenStart + sepMatch[1].length + sepMatch[2].length,
      };
    }
  }
  return { kind: 'prefix', fragment: tokenText, tokenStart, tokenEnd };
}

export function applyTokensToState(tokens, currentState) {
  if (!tokens || tokens.length === 0) return {};
  const updates = {};
  const ensureSet = (key) => {
    if (!updates[key]) updates[key] = new Set(currentState?.[key] || []);
    return updates[key];
  };

  for (const tok of tokens) {
    const k = tok.keyword;
    if (k.kind === 'numeric') {
      updates[k.filterKey] = { op: tok.op, value: parseInt(tok.value, 10) };
    } else if (k.kind === 'name') {
      updates._extraName = (updates._extraName ? updates._extraName + ' ' : '') + tok.value;
    } else if (k.kind === 'string') {
      updates[k.filterKey] = tok.value;
    } else if (k.kind === 'enum') {
      const target = (k.enumAliases && k.enumAliases[tok.value.toLowerCase()])
        || k.enumOptions.find((v) => v.toLowerCase() === tok.value.toLowerCase())
        || tok.value;
      ensureSet(k.filterKey).add(target);
    } else if (k.kind === 'single-enum') {
      // Single-value enum. Replaces the existing value rather than adding
      // to a Set. Used for scope (deck/owned/all) and finish (foil/etc.).
      const target = (k.enumAliases && k.enumAliases[tok.value.toLowerCase()])
        || k.enumOptions.find((v) => v.toLowerCase() === tok.value.toLowerCase())
        || tok.value;
      updates[k.filterKey] = target;
    } else if (k.kind === 'multi-string') {
      ensureSet(k.filterKey).add(tok.value);
    } else if (k.kind === 'element') {
      const set = ensureSet(k.filterKey);
      for (const ch of tok.value.toLowerCase()) {
        if (ELEMENT_LETTER_MAP[ch]) set.add(ELEMENT_LETTER_MAP[ch]);
      }
    }
  }
  return updates;
}

/**
 * Next highlighted row for the suggestion dropdown's arrow-key navigation.
 * A closed dropdown opens on the first row instead of stepping off the
 * stale index, so ArrowDown always means "show me the options".
 *
 * @param {number} idx current highlight index
 * @param {number} count number of suggestions
 * @param {'ArrowDown'|'ArrowUp'} key the pressed key
 * @param {boolean} isOpen whether the dropdown is currently visible
 * @returns {number} the next index, wrapping at both ends
 */
export function nextHighlightIndex(idx, count, key, isOpen) {
  if (count <= 0) return 0;
  if (!isOpen) return 0;
  const step = key === 'ArrowDown' ? 1 : count - 1;
  return (idx + step) % count;
}
